import React, { useState } from "react";
import ProteinContent from "./components/ProteinContent/ProteinContent";
import ProteinFAQ from "./components/ProteinFAQ/ProteinFAQ";
import "./ProteinIntakeCalculator.css";

const ProteinIntakeCalculator = () => {
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("kg"); // Default unit is kg
  const [activityLevel, setActivityLevel] = useState("");
  const [goal, setGoal] = useState("");
  const [proteinIntake, setProteinIntake] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Function to convert weight to kilograms based on the selected unit
  const convertToKilograms = (weight, unit) => {
    switch (unit) {
      case "kg":
        return parseFloat(weight);
      case "lb":
        return parseFloat(weight) * 0.453592;
      case "st":
        return parseFloat(weight) * 6.35029;
      default:
        return 0;
    }
  };

  const validateInputs = () => {
    const weightInKg = convertToKilograms(weight, weightUnit);

    if (isNaN(weightInKg) || weightInKg <= 0) {
      setErrorMessage("Please enter a valid weight.");
      return false;
    }

    if (!activityLevel) {
      setErrorMessage("Please select your activity level.");
      return false;
    }

    if (!goal) {
      setErrorMessage("Please select your fitness goal.");
      return false;
    }

    setErrorMessage(""); // Clear any existing errors
    return true;
  };

  const calculateProteinIntake = () => {
    if (!validateInputs()) {
      return;
    }

    const weightInKg = convertToKilograms(weight, weightUnit);
    let multiplier = 1.2; // Default multiplier for moderate activity and maintenance

    if (activityLevel === "Sedentary") {
      multiplier = 0.8;
    } else if (activityLevel === "Active") {
      multiplier = 1.5;
    }

    if (goal === "Muscle Building") {
      multiplier += 0.5;
    }

    const recommendedProtein = weightInKg * multiplier;
    setProteinIntake(recommendedProtein.toFixed(2));
  };

  const resetCalculator = () => {
    setWeight("");
    setWeightUnit("kg");
    setActivityLevel("");
    setGoal("");
    setProteinIntake(null);
    setErrorMessage("");
  };

  return (
    <div className="protein-calculator">
      <div className="calculator-wrapper">
        <h2 className="calculator-title">Protein Intake Calculator</h2>
        <p className="calculator-description">
          Calculate your daily protein needs based on your weight, activity
          level, and fitness goal.
        </p>

        <div className="engagement-message">
          <p>
            <strong>Optimize Your Protein Intake!</strong>
            Try our{" "}
            <a href="/calculator/advanced-protein-calculator" className="cta-link">
              Advanced Protein Calculator
            </a>{" "}
            for personalized insights.
          </p>
        </div>

        <div className="form-container">
          {errorMessage && <p className="error-message">{errorMessage}</p>}

          <div className="input-container">
            <label htmlFor="weight">Your Weight:</label>
            <div className="weight-input-group">
              <input
                type="number"
                id="weight"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                min="0"
                placeholder="Enter your weight"
                aria-label="Enter your weight"
              />
              <select
                id="weight-unit"
                value={weightUnit}
                onChange={(e) => setWeightUnit(e.target.value)}
                aria-label="Select your weight unit"
              >
                <option value="kg">kg</option>
                <option value="lb">lb</option>
                <option value="st">st</option>
              </select>
            </div>
          </div>

          <div className="input-container">
            <label htmlFor="activity">Activity Level:</label>
            <select
              id="activity"
              value={activityLevel}
              onChange={(e) => setActivityLevel(e.target.value)}
              aria-label="Select your activity level"
            >
              <option value="" disabled>
                -- Select Activity Level --
              </option>
              <option value="Sedentary">
                Sedentary (little or no exercise)
              </option>
              <option value="Moderate">Moderate (light exercise)</option>
              <option value="Active">Active (regular exercise)</option>
            </select>
          </div>

          <div className="input-container">
            <label htmlFor="goal">Goal:</label>
            <select
              id="goal"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              aria-label="Select your fitness goal"
            >
              <option value="" disabled>
                -- Select Goal --
              </option>
              <option value="Maintenance">Maintenance</option>
              <option value="Muscle Building">Muscle Building</option>
            </select>
          </div>

          <div className="protein-button-container">
            <button onClick={calculateProteinIntake} aria-label="Calculate">
              Calculate
            </button>
            <button
              onClick={resetCalculator}
              className="reset-button"
              aria-label="Reset Calculator"
            >
              Reset
            </button>
          </div>
        </div>

        {proteinIntake && (
          <div className="output-container">
            <h2 className="output-title">Recommended Daily Protein Intake:</h2>
            <p className="protein-output-value">{proteinIntake} grams</p>
          </div>
        )}
      </div>

      <ProteinContent />
      <ProteinFAQ />
    </div>
  );
};

export default ProteinIntakeCalculator;
